import { ApolloError } from '@apollo/client';
import { useErrorHandler } from 'hooks/errorHandler';
import { createContext, ReactNode, useContext, useState } from 'react';
import SnackbarContainer from './index';

interface ContextType {
  showError: (text: string) => void;
  showApiError: (error: ApolloError | unknown) => void;
  showInfo: (text: string) => void;
  showSuccess: (text: string) => void;
}
export interface SnackbarItem {
  id: number;
  text: string;
  isError: boolean;
  isSuccess: boolean;
}

const Context = createContext({} as ContextType);

interface Prop {
  children: ReactNode;
}

const SnackbarContext = ({ children }: Prop) => {
  const [items, setItems] = useState<SnackbarItem[]>([]);
  const { getErrorMessage } = useErrorHandler();

  const showError = (text: string) => {
    setItems([
      ...items,
      {
        id: new Date().getTime(),
        text,
        isError: true,
        isSuccess: false,
      },
    ]);
  };

  const showApiError = (error: ApolloError | unknown) => {
    showError(getErrorMessage(error as ApolloError));
  };

  const showInfo = (text: string) => {
    setItems([
      ...items,
      {
        id: new Date().getTime(),
        text,
        isError: false,
        isSuccess: false,
      },
    ]);
  };

  const showSuccess = (text: string) => {
    setItems([
      ...items,
      {
        id: new Date().getTime(),
        text,
        isError: false,
        isSuccess: true,
      },
    ]);
  };

  const onCloseSnackbar = (id: number) => {
    const newItems = items.filter((item) => item.id !== id);
    setItems(newItems);
  };

  // we need to memoize the 'value' exactly once because unwanted re-renders will be triggered otherwise
  const value = { showError, showApiError, showInfo, showSuccess };
  return (
    <Context.Provider value={value}>
      {children}
      <SnackbarContainer items={items} onClose={onCloseSnackbar} />
    </Context.Provider>
  );
};

export const useContextSnackbar = () => {
  return useContext(Context);
};

export default SnackbarContext;
